import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {gsApi} from "../auth/auth";
import {
  getOpts,
  getResponse,
  getSerializedData,
  shortDelay,
} from "../helpers/JSONapi";

import {
  deleteThunkResponse,
  getThunkResponse,
  saveThunkResponse,
  sponsors,
  sponsorshipTiers,
  thunkResponse,
  tournament,
} from "../app/api";

export const getTournamentSponsorshipTiers = createAsyncThunk(
  "sponsorshipTiers/getTournamentSponsorshipTiers",
  async (tournamentID, thunkAPI) => {
    const response = await gsApi.get(
      `${tournament}/${tournamentID}/sponsorship-tiers`
    );
    return response;
  }
);

export const getTournamentSponsorshipTier = createAsyncThunk(
  "sponsorshipTiers/getTournamentSponsorshipTier",
  async (sponsorhipTierID, thunkAPI) => {
    const response = await gsApi.get(`${sponsorshipTiers}/${sponsorhipTierID}`);
    return response;
  }
);

export const getSponsorshipTierSponsors = createAsyncThunk(
  "sponsorshipTiers/getSponsorshipTierSponsors",
  async (sponsorshipTierID, thunkAPI) => {
    const response = await gsApi.get(
      `${sponsorshipTiers}/${sponsorshipTierID}`,
      {
        data: {},
      }
    );
    return response.sponsors;
  }
);

export const getTournamentSponsors = createAsyncThunk(
  "sponsorshipTiers/getTournamentSponsors",
  async (tournamentID, thunkAPI) => {
    const response = await gsApi.get(`${tournament}/${tournamentID}/sponsors`);
    return response;
  }
);

export const createTournamentSponsorshipTier = createAsyncThunk(
  "sponsorshipTiers/createTournamentSponsorshipTier",
  async (sponsorshipTier, thunkAPI) => {
    let serializedData = getSerializedData(
      sponsorshipTier,
      "sponsorship-tiers",
      getOpts(sponsorshipTier, [...getTournamentRelationship()])
    );
    return await getResponse(
      gsApi.post,
      `${sponsorshipTiers}`,
      serializedData,
      thunkAPI
    );
  }
);

export const saveTournamentSponsorshipTier = createAsyncThunk(
  "sponsorshipTiers/saveTournamentSponsorshipTier",
  async (sponsorshipTier, thunkAPI) => {
    let serializedData = getSerializedData(
      sponsorshipTier,
      "sponsorship-tiers",
      getOpts(sponsorshipTier, [...getTournamentRelationship()])
    );
    return await getResponse(
      gsApi.patch,
      `${sponsorshipTiers}/${sponsorshipTier.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const saveTournamentSponsorshipTierOrder = createAsyncThunk(
  "sponsorshipTiers/saveTournamentSponsorshipTierOrder",
  async (sponsorshipTier, thunkAPI) => {
    let serializedData = getSerializedData(
      sponsorshipTier,
      "sponsorship-tiers",
      getOpts(sponsorshipTier, [...getTournamentRelationship()])
    );
    return await getResponse(
      gsApi.patch,
      `${sponsorshipTiers}/${sponsorshipTier.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const saveSponosor = createAsyncThunk(
  "sponsorshipTiers/saveSponosor",
  async (sponsor, thunkAPI) => {
    let serializedData = getSerializedData(
      sponsor,
      "sponsorship-tiers",
      getOpts(sponsor, [
        ...getTournamentRelationship(),
        ...getSponsorshipTierRelationship(),
        ...getSponsorRelationship(),
      ])
    );
    return await getResponse(
      gsApi.patch,
      `${sponsors}/${sponsor.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const removeTournamentSponsorshipTier = createAsyncThunk(
  "sponsorshipTiers/removeTournamentSponsorshipTier",
  async (sponsorshipTierID, thunkAPI) => {
    const response = await gsApi.delete(
      `${sponsorshipTiers}/${sponsorshipTierID}`,
      {
        data: {},
      }
    );
    return response;
  }
);

export const sponsorshipTierSlice = createSlice({
  name: "sponsorshipTiers",
  initialState: {
    sponsorshipTierList: [],
    currentSponsorshipTier: {},
    currentSponsorshipTierSponsors: [],
    tournamentSponsors: [],
    notifications: [],
    loading: [],
  },
  reducers: {
    setSponsorshipTierList: (state, action) => {
      state.sponsorshipTierList = action.payload;
    },
    setTournamentSponsor: (state, action) => {
      const index = state.tournamentSponsors.findIndex?.(
        (ts) => ts.id === action.payload.id
      );
      state.tournamentSponsors[index] = action.payload;
    },
    setCurrentSponsorshipTier: (state, action) => {
      state.currentSponsorshipTier = action.payload;
    },
    setCurrentSponsorshipTierSponsors: (state, action) => {
      state.currentSponsorshipTierSponsors = action.payload;
    },
    setTournamentSponsors: (state, action) => {
      state.tournamentSponsors = action.payload;
    },
    addNotification: (state, action) => {
      state.notifications.push(action.payload);
    },
    clearNotifications: (state, action) => {
      state.notifications = [];
    },
  },
  extraReducers: {
    ...getThunkResponse(getTournamentSponsorshipTiers, (state, action) => {
      state.sponsorshipTierList = action.payload;
    }),
    ...getThunkResponse(getTournamentSponsors, (state, action) => {
      state.tournamentSponsors = action.payload;
    }),
    ...getThunkResponse(getTournamentSponsorshipTier, (state, action) => {
      state.currentSponsorshipTier = action.payload;
    }),
    ...getThunkResponse(getSponsorshipTierSponsors, (state, action) => {
      state.currentSponsorshipTierSponsors = action.payload;
    }),
    ...thunkResponse(
      createTournamentSponsorshipTier,
      (state, action) => {
        state.sponsorshipTierList = [
          ...state.sponsorshipTierList,
          action.payload,
        ];
      },
      "New Sponsorship Tier",
      {
        pendingText: "Creating Sponsorship Tier...",
        pendingDelay: shortDelay,
        fulfilledText: "Tournament Sponsorship Tier has been created!",
        fulfilledDelay: shortDelay,
        rejectedDelay: 0,
      }
    ),
    ...thunkResponse(
      saveTournamentSponsorshipTier,
      (state, action) => {
        const sponsorshipTierIndex = state.sponsorshipTierList.findIndex(
          (st) => st.id === action.payload.id
        );
        state.sponsorshipTierList[sponsorshipTierIndex] = action.payload;
      },
      "Sponsorship Tier",
      {
        pendingText: "Saving Sponsorship Tier...",
        pendingDelay: shortDelay,
        fulfilledText: "Sponsorship Tier has been saved!",
        fulfilledDelay: shortDelay,
        rejectedDelay: 0,
      }
    ),
    ...saveThunkResponse(
      saveSponosor,
      (state, action) => {
        const sponsorIndex = state.tournamentSponsors.findIndex(
          (s) => s.id === action.payload.id
        );
        state.tournamentSponsors[sponsorIndex] = {
          ...state.tournamentSponsors[sponsorIndex],
          ...action.payload,
        };
      },
      "Sponsor Assignment"
    ),
    ...deleteThunkResponse(
      removeTournamentSponsorshipTier,
      (state, action) => {
        state.sponsorshipTierList = state.sponsorshipTierList.filter?.(
          (t) => t.id !== action.meta.arg
        );
        state.currentSponsorshipTier = {};
      },
      "Sponsorship Tier"
    ),
    ...saveThunkResponse(
      saveTournamentSponsorshipTierOrder,
      (state, action) => {
        const sponsorshipTierIndex = state.sponsorshipTierList.findIndex(
          (s) => s.id === action.payload.id
        );
        state.sponsorshipTierList[sponsorshipTierIndex] = action.payload;
      },
      "Sponsorship Tier"
    ),
  },
});

export const {
  setSponsorshipTierList,
  setTournamentSponsor,
  setCurrentSponsorshipTier,
  setCurrentSponsorshipTierSponsors,
  setTournamentSponsors,
  addNotification,
  clearNotifications,
} = sponsorshipTierSlice.actions;

export const selectSponsorshipTierList = (state) =>
  state?.[sponsorshipTierSlice.name]?.sponsorshipTierList;

export const selectCurrentSponsorshipTier = (state) =>
  state?.[sponsorshipTierSlice.name]?.currentSponsorshipTier;

export const selectCurrentSponsorshipTierSponsors = (state) =>
  state?.[sponsorshipTierSlice.name]?.currentSponsorshipTierSponsors;

export const selectSponsorshipTierNotifications = (state) =>
  state?.[sponsorshipTierSlice.name]?.notifications;

export const selectTournamentSponsors = (state) =>
  state?.[sponsorshipTierSlice.name]?.tournamentSponsors;

export const selectLoading = (state) =>
  state?.[sponsorshipTierSlice.name]?.loading;

export default sponsorshipTierSlice.reducer;

function getTournamentRelationship() {
  const relationships = [];
  relationships.push({
    name: "tournament",
    opt: { ref: "id", included: true },
  });
  return relationships;
}

function getSponsorshipTierRelationship() {
  const relationships = [];
  relationships.pusn({
    name: "sponsorshipTier",
    opt: { ref: "id", included: true },
  });
  return relationships;
}

function getSponsorRelationship() {
  const relationships = [];
  relationships.push({
    name: "sponsor",
    opt: { ref: "id", included: true },
  });
  return relationships;
}
